import { titleCase } from '@nsb/core';
import { dateWords } from '../../days.ts';

/** A step's or a task's days, as its pencil changes one after the fact. */
interface Dated {
  title: string;
  startedOn: string | null;
  doneOn: string | null;
}

/**
 * Whether a step's day is its pencil's to set, the API's rule (`updateStep`): every step's and task's (P-32, #114; Jason,
 * 2026-10-27: "i marked retainer received, but i couldn't back date it when importing a project"), a gate's done day once
 * NSB marked it done, its own or on an override; never a gate the client approved, which keeps the day they approved it,
 * pressed in the portal or on its approval record; and a gate, done or not done yet, has no day it went under way.
 */
export function dayByHand(s: { kind: string; gate?: { passed: { approved: boolean } | null } | null }, field: 'startedOn' | 'doneOn'): boolean {
  if (s.kind !== 'gate') return true;
  return field === 'doneOn' && !s.gate?.passed?.approved;
}

/**
 * The days a step's pencil may set, the API's rule (`updateStep`, L-22 A): never a day still to come; done never before
 * a start set apart, while a start that came with its done mark (a task marked straight to done) moves back with it, so
 * it bounds nothing (P-23, #86); under way never after the day it was done.
 */
export function dayBounds(s: Dated, field: 'startedOn' | 'doneOn', today: string): { min: string | undefined; max: string } {
  if (field === 'startedOn') return { min: undefined, max: s.doneOn && s.doneOn < today ? s.doneOn : today };
  const apart = s.startedOn !== null && s.startedOn !== s.doneOn;
  return { min: apart ? s.startedOn! : undefined, max: today };
}

/** Why a day cannot be set, in words, or null: said beside Save, never by a Save held back (P-23). */
export function dayRefusal(s: Dated, field: 'startedOn' | 'doneOn', day: string, today: string): string | null {
  const { min, max } = dayBounds(s, field, today);
  if (day > today) return 'That day is still to come: a step is marked only on a day that has been.';
  const name = titleCase(s.title);
  if (field === 'startedOn' && day > max) return `${name} was done ${dateWords(max, 'dayOrDate', { today })}: it cannot have gone under way after that.`;
  if (field === 'doneOn' && min !== undefined && day < min) {
    return `${name} went under way ${dateWords(min, 'dayOrDate', { today })}: it cannot be done before that. Set the day it went under way first.`;
  }
  return null;
}
